'use client';

import { Square, X } from 'lucide-react';

import { Button } from '@/components/ui/button';

import { Tooltip } from '@/shared/components/tooltip';

import { cn } from '@/lib/utils';

import { MAX_RECORDING_TIME } from '../constants';
import { formatTime } from '../utils';
import { useVideoRecording } from '../video-recording-provider';
import { AudioWaveIndicator } from './audio-wave-indicator';
import { DevicePickers } from './device-pickers';
import { VideoContainer } from './shared';

/**
 * Combined view for 'camera' and 'recording' states
 * Uses a single video element to maintain srcObject across state changes
 */
export function LiveView() {
	const {
		videoRef,
		uiState,
		recordingState,
		closeCamera,
		startRecording,
		stopRecording,
		isRecordingBlocked,
		trackNoAudioDetected,
	} = useVideoRecording();

	const isRecording = uiState.state === 'recording';
	// Use object-contain for portrait to show full frame, object-cover for landscape
	const objectFit = uiState.isPortrait ? 'object-contain' : 'object-cover';

	return (
		<div className="flex flex-col gap-2">
			<VideoContainer
				overlay={
					isRecording ? (
						<div className="flex items-center gap-2">
							<div className="flex h-7 items-center gap-2 rounded-full bg-white/70 p-2 text-primary backdrop-blur-sm">
								<span className="text-xs font-medium">
									{formatTime(recordingState.recordingTime)} / {formatTime(MAX_RECORDING_TIME)}
								</span>
							</div>
							<Tooltip text="Stop recording">
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={stopRecording}
									className="h-7 rounded-full bg-red-400/90 text-primary text-red-800 backdrop-blur-sm transition-colors hover:bg-red-400/80"
								>
									<Square
										height={16}
										width={16}
										strokeWidth={3}
									/>
								</Button>
							</Tooltip>
						</div>
					) : (
						<Button
							type="button"
							variant="ghost"
							onClick={startRecording}
							disabled={!uiState.cameraReady || isRecordingBlocked}
							className="bg-white/70 text-primary backdrop-blur-sm transition-colors hover:bg-white/60"
						>
							Start Recording
						</Button>
					)
				}
			>
				{/* Single video element that persists across camera/recording states */}
				<video
					ref={videoRef}
					autoPlay
					muted
					playsInline
					className={`h-64 w-full md:h-80 lg:h-96 ${objectFit}`}
					style={{
						display: uiState.showVideo ? 'block' : 'none',
						transform: 'scaleX(-1)',
					}}
				/>
				{/* Bottom-left, same height as the CTA row so they line up */}
				{uiState.cameraReady && (
					<div
						className={cn(
							'absolute bottom-2 left-2 flex items-center',
							isRecording ? 'h-7' : 'h-10'
						)}
					>
						<AudioWaveIndicator
							// Remount on record start so silence from the preview doesn't count
							key={isRecording ? 'recording' : 'camera'}
							stream={recordingState.stream}
							showMicIcon={!isRecording}
							showSilenceNudge={isRecording}
							onSilenceNudge={trackNoAudioDetected}
						/>
					</div>
				)}
				{/* Close button - only show when not recording */}
				{!isRecording && (
					<button
						onClick={closeCamera}
						className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/30 backdrop-blur-sm transition-colors hover:bg-white/50"
						aria-label="Close camera"
					>
						<X className="h-4 w-4 text-primary" />
					</button>
				)}
			</VideoContainer>
			{/* Device switching is pre-recording only */}
			{!isRecording && <DevicePickers />}
		</div>
	);
}
